import { useCallback } from 'react'
import type { RouterOutputs } from '@/lib/trpc'
import type { ProblemTopic } from '../../../../lib/problems/problem-topics.js'
import { trpc } from '../../../../lib/trpc.js'
import type { DifficultyInfo } from '../../problems/types'
import { useResource } from './use-resource'

export interface StudyPlanProblem {
  id: string
  title: string
  slug: string
  difficulty: DifficultyInfo | null
  source: string
  externalId?: string | null
  url?: string | null
  topics?: ProblemTopic[]
}

type StudyPlanListRow =
  RouterOutputs['admin']['studyPlans']['list']['data'][number]

/**
 * One system-design problem on a study plan, as the router returns it.
 *
 * Derived, not re-declared. This branch renamed the field this shape carries
 * (free-text `company` → `companyTags`, LG-930) on both sides by hand, and the
 * assertion below is what stopped the checker verifying the rename: had the
 * router kept returning tag objects, the `.join(', ')` in
 * `system-design-study-plans-section` would have rendered `[object Object]`
 * with no type error anywhere. Note `companyTags` here is `string[]` — tag
 * *names* — where the content router sends `{id,name,slug}[]`.
 */
export type SystemDesignStudyPlanProblem = NonNullable<
  StudyPlanListRow['systemDesignProblems']
>[number]

export interface StudyPlanResourceItem {
  id: string
  title: string
  url: string
  type: string
  description: string | null
  icon: string | null
  order: number
}

export interface StudyPlanSource {
  id: string
  name: string
  icon: string | null
  url: string | null
}

export interface StudyPlan {
  id: string
  name: string
  description?: string | null
  icon?: string | null
  url?: string | null
  type?: 'coding' | 'system_design'
  source?: StudyPlanSource | null
  createdAt: string
  updatedAt: string
  problemCount: number
  problems: StudyPlanProblem[]
  systemDesignProblems?: SystemDesignStudyPlanProblem[]
  resources?: StudyPlanResourceItem[]
}

export interface StudyPlanGroup {
  sourceId: string
  sourceName: string
  sourceIcon: string | null
  plans: StudyPlan[]
}

const LEGACY_CALIBRATION_PLAN_NAME_MAP: Record<string, string> = {
  'Calibration Questions - EASY': 'Calibration Questions - INTERMEDIATE',
  'Calibration Questions - HARD': 'Calibration Questions - ADVANCED',
}

export function normalizeCalibrationPlanName(name: string): string {
  return LEGACY_CALIBRATION_PLAN_NAME_MAP[name] ?? name
}

export function groupStudyPlansBySource(plans: StudyPlan[]): StudyPlanGroup[] {
  const groups = new Map<string, StudyPlanGroup>()
  const ungrouped: StudyPlan[] = []

  for (const plan of plans) {
    if (plan.source) {
      const existing = groups.get(plan.source.id)
      if (existing) {
        existing.plans.push(plan)
      } else {
        groups.set(plan.source.id, {
          sourceId: plan.source.id,
          sourceName: plan.source.name,
          sourceIcon: plan.source.icon,
          plans: [plan],
        })
      }
    } else {
      ungrouped.push(plan)
    }
  }

  const result = Array.from(groups.values())
  if (ungrouped.length > 0) {
    result.push({
      sourceId: '_ungrouped',
      sourceName: 'Other',
      sourceIcon: null,
      plans: ungrouped,
    })
  }
  return result
}

export interface StudyPlanFormData {
  name: string
  description?: string | null
  icon?: string | null
  url?: string | null
}

export function useStudyPlans(
  sourceId?: string,
  type?: 'coding' | 'system_design',
) {
  const utils = trpc.useUtils()

  const invalidateList = useCallback(() => {
    utils.admin.studyPlans.list.invalidate()
  }, [utils])

  // Filter by type client-side (matching original behavior). The router types
  // `data` more loosely than the local StudyPlan interface.
  const selectStudyPlans = useCallback(
    (
      data: RouterOutputs['admin']['studyPlans']['list'] | undefined,
    ): StudyPlan[] => {
      const normalized = (data?.data ?? []).map((sp) => ({
        ...sp,
        name: normalizeCalibrationPlanName(sp.name),
      }))
      return type ? normalized.filter((sp) => sp.type === type) : normalized
    },
    [type],
  )

  const {
    items: studyPlans,
    loading,
    error,
    create,
    update,
    remove,
  } = useResource({
    query: trpc.admin.studyPlans.list.useQuery(
      sourceId ? { sourceId } : undefined,
    ),
    select: selectStudyPlans,
    invalidate: invalidateList,
    create: trpc.admin.studyPlans.create,
    update: trpc.admin.studyPlans.update,
    remove: trpc.admin.studyPlans.delete,
  })

  const addProblemsMutation = trpc.admin.studyPlans.addProblems.useMutation({
    onSuccess: () => invalidateList(),
  })

  const removeProblemsMutation =
    trpc.admin.studyPlans.removeProblems.useMutation({
      onSuccess: () => invalidateList(),
    })

  const addSystemDesignProblemsMutation =
    trpc.admin.studyPlans.addSystemDesignProblems.useMutation({
      onSuccess: () => invalidateList(),
    })

  const removeSystemDesignProblemsMutation =
    trpc.admin.studyPlans.removeSystemDesignProblems.useMutation({
      onSuccess: () => invalidateList(),
    })

  const addResourcesMutation = trpc.admin.studyPlans.addResources.useMutation({
    onSuccess: () => invalidateList(),
  })

  const removeResourcesMutation =
    trpc.admin.studyPlans.removeResources.useMutation({
      onSuccess: () => invalidateList(),
    })

  const createStudyPlan = useCallback(
    async (data: StudyPlanFormData): Promise<StudyPlan> => {
      const payload: {
        name: string
        description?: string
        icon?: string
        url?: string
        sourceId?: string
        type?: 'coding' | 'system_design'
      } = {
        name: data.name,
        description: data.description ?? undefined,
        icon: data.icon ?? undefined,
        url: data.url ?? undefined,
        type,
      }
      if (sourceId) payload.sourceId = sourceId
      const result = await create(payload)
      return result
    },
    [create, sourceId, type],
  )

  // The update procedure only patches name/description/icon/url and returns
  // that narrow row — it does not re-join problems/source/resources, so its
  // real output is narrower than the rich `StudyPlan` shape (which callers
  // never read off this return value; they rely on list invalidation).
  const updateStudyPlan = useCallback(
    async (
      id: string,
      data: Partial<StudyPlanFormData>,
    ): Promise<RouterOutputs['admin']['studyPlans']['update']> => {
      const result = await update({ id, ...data })
      return result
    },
    [update],
  )

  const deleteStudyPlan = useCallback(
    async (id: string): Promise<void> => {
      await remove({ id })
    },
    [remove],
  )

  const getStudyPlan = useCallback(
    async (id: string): Promise<StudyPlan> => {
      const result = await utils.admin.studyPlans.getById.fetch({ id })
      return result
    },
    [utils],
  )

  const addProblemsToStudyPlan = useCallback(
    async (
      studyPlanId: string,
      problemIds: string[],
      addSourceId?: string,
    ): Promise<{
      added: number
      skipped: number
      sourcesLinked?: number
      sourcesSkipped?: number
    }> => {
      const input: { id: string; problemIds: string[]; sourceId?: string } = {
        id: studyPlanId,
        problemIds,
      }
      if (addSourceId) input.sourceId = addSourceId
      const result = await addProblemsMutation.mutateAsync(input)
      return result
    },
    [addProblemsMutation],
  )

  const removeProblemsFromStudyPlan = useCallback(
    async (studyPlanId: string, problemIds: string[]): Promise<void> => {
      await removeProblemsMutation.mutateAsync({
        id: studyPlanId,
        problemIds,
      })
    },
    [removeProblemsMutation],
  )

  const addSystemDesignProblemsToStudyPlan = useCallback(
    async (
      studyPlanId: string,
      systemDesignProblemIds: string[],
    ): Promise<{ added: number; skipped: number }> => {
      const result = await addSystemDesignProblemsMutation.mutateAsync({
        id: studyPlanId,
        systemDesignProblemIds,
      })
      return result
    },
    [addSystemDesignProblemsMutation],
  )

  const removeSystemDesignProblemsFromStudyPlan = useCallback(
    async (
      studyPlanId: string,
      systemDesignProblemIds: string[],
    ): Promise<void> => {
      await removeSystemDesignProblemsMutation.mutateAsync({
        id: studyPlanId,
        systemDesignProblemIds,
      })
    },
    [removeSystemDesignProblemsMutation],
  )

  const addResourcesToStudyPlan = useCallback(
    async (studyPlanId: string, resourceIds: string[]) => {
      await addResourcesMutation.mutateAsync({
        id: studyPlanId,
        resourceIds,
      })
    },
    [addResourcesMutation],
  )

  const removeResourcesFromStudyPlan = useCallback(
    async (studyPlanId: string, resourceIds: string[]) => {
      await removeResourcesMutation.mutateAsync({
        id: studyPlanId,
        resourceIds,
      })
    },
    [removeResourcesMutation],
  )

  return {
    studyPlans,
    loading,
    error,
    refresh: invalidateList,
    createStudyPlan,
    updateStudyPlan,
    deleteStudyPlan,
    getStudyPlan,
    addProblemsToStudyPlan,
    removeProblemsFromStudyPlan,
    addSystemDesignProblemsToStudyPlan,
    removeSystemDesignProblemsFromStudyPlan,
    addResourcesToStudyPlan,
    removeResourcesFromStudyPlan,
  }
}
